<script setup lang="ts">
    import { reactive, computed, onMounted } from 'vue';
    import Loading from '@shared/components/Loading.vue';
    import FormInput from '@shared/components/Form/FormInput.vue';
    import FormSelect from '@shared/components/Form/FormSelect.vue';
    import FormCheckable from '@shared/components/Form/FormCheckable.vue';
    import { SwitchStyle } from '@shared/components/Form/Form';
    import MultiSelect from '@shared/components/MultiSelect/MultiSelect.vue';
    import List from '@shared/components/List/List.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { bsAlert, AlertTypes } from '@shared/components/bsAlerts';
    import { vTooltip } from '@shared/directives';
    import { loadTags } from '@shared/components/Tags/Tags.access';
    import { type Tag } from '@shared/components/Tags/TagsTypes';
    import { type CurrentAccountReference } from '@shared/Root/Context.access';
    import {
        getDataSources,
        saveDataSource,
        validateDataSourceUrl,
        previewDataSource,
        type DataSourceListItem,
        type DataSourceSaveModel,
    } from './AccountDataSources.access';

    const props = defineProps<{
        account: CurrentAccountReference;
    }>();

    const DataSourceType = {
        Rss: 2,
        Feed20: 35,
    };

    const state = reactive<{
        isLoading: boolean;
        isSaving: boolean;
        loaded: boolean;
        dataSources: DataSourceListItem[];
        tags: Tag[];
        editing: DataSourceSaveModel | null;
        isEventDataSource: boolean;
        eventParams: { tagIds: string[]; includeDays: string; limit: string };
        previewMode: boolean;
        previewJson: string;
    }>({
        isLoading: false,
        isSaving: false,
        loaded: false,
        dataSources: [],
        tags: [],
        editing: null,
        isEventDataSource: false,
        eventParams: { tagIds: [], includeDays: '14', limit: '10' },
        previewMode: false,
        previewJson: '',
    });

    onMounted(async () => {
        if (state.loaded) return;

        state.isLoading = true;

        const [dataSources, tags] = await Promise.all([getDataSources(), loadTags()]);

        state.dataSources = dataSources;
        state.tags = tags;
        state.loaded = true;
        state.isLoading = false;
    });

    const eventBaseUrl = computed(() => `${props.account.PublicEventUrl.PublicHost}/Api/Events/${props.account.AccountId}/Feed/`);

    const eventDataSourceUrl = computed(() => {
        if (!state.isEventDataSource || !state.editing) return '';

        const tagIds = state.eventParams.tagIds.length > 0 ? '&tagids=' + state.eventParams.tagIds.join(',') : '';
        const type = state.editing.Type == DataSourceType.Feed20 ? 'rss' : 'xml';

        return `${eventBaseUrl.value}${type}?includedays=${state.eventParams.includeDays}&limit=${state.eventParams.limit}${tagIds}`;
    });

    const sameVariableName = computed(() => {
        if (!state.editing) return false;

        return state.dataSources.some((ds) => ds.DataSourceId !== state.editing!.DataSourceId && ds.VariableName === state.editing!.VariableName);
    });

    function typeLabel(type: number): string {
        return type == DataSourceType.Feed20 ? $Resources.DataSourceType35 : $Resources.DataSourceType2;
    }

    function validatorUrl(url: string): string {
        return 'https://validator.w3.org/feed/check.cgi?url=' + encodeURIComponent(url);
    }

    function addNew(): void {
        state.editing = { DataSourceId: null, Name: '', VariableName: '', Url: '', Type: DataSourceType.Feed20 };
        parseEventUrl('');
    }

    function edit(dataSource: DataSourceListItem): void {
        state.editing = {
            DataSourceId: dataSource.DataSourceId,
            Name: dataSource.Name,
            VariableName: dataSource.VariableName,
            Url: dataSource.Url,
            Type: dataSource.Type,
        };
        parseEventUrl(dataSource.Url);
    }

    function cancelEdit(): void {
        state.editing = null;
    }

    function parseEventUrl(url: string): void {
        state.isEventDataSource = false;

        let tagIds: string[] = [];
        let includeDays = '14';
        let limit = '10';

        if (url && url.startsWith(eventBaseUrl.value)) {
            state.isEventDataSource = true;

            const searchParams = new URLSearchParams(url.split('?')[1]);
            tagIds = searchParams.get('tagids') ? searchParams.get('tagids')!.split(',') : tagIds;
            includeDays = searchParams.get('includedays') || includeDays;
            limit = searchParams.get('limit') || limit;
        }

        state.eventParams = { tagIds, includeDays, limit };
    }

    function onIsEventDataSourceChange(newVal: boolean): void {
        state.isEventDataSource = newVal;

        if (newVal && state.editing && !state.editing.DataSourceId) {
            state.editing.Type = DataSourceType.Rss;
        }
    }

    function createVariableName(): string {
        return crypto.randomUUID().replaceAll('-', '');
    }

    async function save(): Promise<void> {
        if (!state.editing || state.isSaving) return;

        state.isSaving = true;

        try {
            const url = state.isEventDataSource ? eventDataSourceUrl.value : state.editing.Url;
            const validation = await validateDataSourceUrl(url);

            if (validation.Status !== 'OK') {
                bsAlert({ type: AlertTypes.Danger, content: 'Invalid RSS Feed: ' + validation.Message });
                return;
            }

            const payload: DataSourceSaveModel = {
                DataSourceId: state.editing.DataSourceId,
                Url: url,
                Name: state.editing.Name,
                Type: state.editing.Type,
                VariableName: state.editing.VariableName || createVariableName(),
            };

            const saved = await saveDataSource(payload);

            if (!payload.DataSourceId) {
                state.dataSources.push(saved);
            } else {
                const index = state.dataSources.findIndex((ds) => ds.DataSourceId === payload.DataSourceId);
                if (index > -1) state.dataSources[index] = saved;
            }

            state.editing = null;
            bsAlert();
        } catch {
            bsAlert({ type: AlertTypes.Danger });
        } finally {
            state.isSaving = false;
        }
    }

    async function openPreview(variableName: string): Promise<void> {
        if (state.isLoading) return;

        state.isLoading = true;
        state.previewMode = true;
        state.previewJson = '';

        try {
            const data = await previewDataSource(variableName);
            state.previewJson = JSON.stringify(data, null, 4);
        } catch {
            bsAlert({ type: AlertTypes.Danger });
        } finally {
            state.isLoading = false;
        }
    }

    function closePreview(): void {
        state.previewMode = false;
    }
</script>

<template>
    <div v-show="!state.previewMode">
        <template v-if="!state.editing">
            <h2 class="h3">{{ $Resources.DataSourcesHeading }}</h2>
            <p>{{ $Resources.DataSourcesInfoText }}</p>

            <List :items="state.dataSources" itemId="DataSourceId" orderByValue="Modified" orderByDirection="desc" hideSearch>
                <template #toolbar>
                    <button class="btn btn-primary" @click="addNew">{{ $Resources.AddNewDataSourceBtn }}</button>
                </template>
                <template #item="{ item: dataSource }">
                    <div class="flex-1">
                        <h3 class="h4 d-flex flex-wrap gap-xs align-items-baseline">
                            <a href="javascript:void(0)" role="button" @click="edit(dataSource)">{{ dataSource.Name }}</a>
                            <small>{{ typeLabel(dataSource.Type) }}</small>

                            <button
                                type="button"
                                class="btn btn-subtle btn-xs"
                                v-tooltip.top="$Resources.PreviewButton"
                                @click="openPreview(dataSource.VariableName)">
                                <span class="glyphicon glyphicon-eye-open" aria-hidden="true"></span>
                            </button>
                        </h3>

                        <p>{{ dataSource.Url }}</p>
                    </div>

                    <a :href="validatorUrl(dataSource.Url)" target="_blank" rel="noopener" class="align-self-center">
                        {{ $Resources.DataSourceValidateLabel }}
                        <span class="glyphicon glyphicon-new-window c-light text-md m-xxs-l" aria-hidden="true"></span>
                    </a>
                </template>
            </List>
        </template>

        <form v-if="state.editing" @submit.prevent="save">
            <h2 class="h3">
                {{ state.editing.DataSourceId ? $Resources.DataSourceEditHeading : $Resources.DataSourceAddHeading }}
            </h2>

            <FormInput
                class="form-group"
                :label="$Resources.DataSourceName"
                :modelValue="state.editing.Name"
                :required="true"
                @update:modelValue="(newVal: string) => (state.editing!.Name = newVal)"></FormInput>

            <div v-if="state.editing.DataSourceId" class="form-group">
                <FormInput
                    :label="$Resources.DataSourceVariableName"
                    :modelValue="state.editing.VariableName"
                    :required="true"
                    :maxlength="50"
                    pattern="[a-zA-Z0-9]{1,50}"
                    @update:modelValue="(newVal: string) => (state.editing!.VariableName = newVal)"></FormInput>

                <p class="small m-sm-t">{{ $Resources.DataSourceVariableNameRules }}</p>

                <BsAlert
                    v-if="sameVariableName"
                    :type="AlertTypes.Danger"
                    :dismissible="false"
                    :title="$Resources.ErrorTitleLabel"
                    :content="$Resources.DataSourceSameNameAlert"></BsAlert>
            </div>

            <div class="form-group">
                <FormCheckable
                    :label="$Resources.EventDataSourceLabel"
                    :switchStyle="SwitchStyle.Standard"
                    :checked="state.isEventDataSource"
                    @update:modelValue="(newVal: boolean) => onIsEventDataSourceChange(newVal)"></FormCheckable>
            </div>

            <template v-if="!state.isEventDataSource">
                <FormInput
                    class="form-group"
                    type="url"
                    :label="$Resources.DataSourceUrl"
                    placeholder="https://example.com/blog/feed"
                    :required="true"
                    :modelValue="state.editing.Url"
                    @update:modelValue="(newVal: string) => (state.editing!.Url = newVal)"></FormInput>

                <FormSelect
                    class="form-group"
                    :label="$Resources.DataSourceType"
                    :required="true"
                    :modelValue="String(state.editing.Type)"
                    :options="[
                        { label: $Resources.DataSourceType35, value: String(DataSourceType.Feed20) },
                        { label: $Resources.DataSourceType2, value: String(DataSourceType.Rss) },
                    ]"
                    @update:modelValue="(newVal: string) => (state.editing!.Type = Number(newVal))"></FormSelect>
            </template>

            <template v-else>
                <FormInput
                    class="form-group"
                    type="number"
                    :label="$Resources.DataSourceLimit"
                    :modelValue="state.eventParams.limit"
                    @update:modelValue="(newVal: string) => (state.eventParams.limit = newVal)"></FormInput>

                <FormInput
                    class="form-group"
                    type="number"
                    :label="$Resources.DataSourceIncludeDays"
                    :modelValue="state.eventParams.includeDays"
                    @update:modelValue="(newVal: string) => (state.eventParams.includeDays = newVal)"></FormInput>

                <MultiSelect
                    class="form-group"
                    :label="$Resources.EventDataSourceTagsLabel"
                    :items="state.tags"
                    selectProp="TagId"
                    displayProp="Title"
                    selectOnly="TagId"
                    :selectedItems="state.eventParams.tagIds"
                    @update:selectedItems="(newVal: string[]) => (state.eventParams.tagIds = newVal)" />

                <p class="m-lg-b">
                    <span class="label-look d-block">{{ $Resources.DataSourceUrl }}:</span>
                    <a :href="eventDataSourceUrl" rel="noopener" target="_blank">{{ eventDataSourceUrl }}</a>
                </p>
            </template>

            <BsAlert :type="AlertTypes.Info" :dismissible="false" :content="$Resources.DataSourceEmailCompatibilityInfo"></BsAlert>

            <div class="form-group d-flex gap-sm flex-wrap">
                <button type="button" class="btn btn-subtle m-auto-l" @click="cancelEdit">{{ $Resources.CancelButton }}</button>
                <button type="submit" class="btn btn-primary" :disabled="state.isSaving || sameVariableName">{{ $Resources.SaveButton }}</button>
            </div>
        </form>
    </div>

    <div v-if="state.previewMode">
        <p class="fw-600 m-lg-t">
            <a href="javascript:void(0)" role="button" @click="closePreview">
                <span class="glyphicon glyphicon-chevron-left m-xxs-r c-light" aria-hidden="true"></span>
                {{ $Resources.BackButton }}
            </a>
        </p>

        <h2 class="h3 m-lg-b">{{ $Resources.DataSourcePreviewHeading }}</h2>

        <pre>{{ state.previewJson }}</pre>
    </div>

    <Loading :show="state.isLoading || state.isSaving" loadingStyle="progress-bar" fixed></Loading>
</template>
